import { z } from 'zod'
import { startOfDay } from 'date-fns'
import { isEmpty, isNil } from 'lodash'
import type { Company, PaymentTermOption } from './companyModel'
import { paymentTermOptionsList } from './companyModel'
import type { Contact, Identifier } from './debtorModels'
import { ContactSchema } from './debtorModels'
import type { LoanGateway } from './loanModels'
import type { Currency } from './moneyModel'
import type { Root } from './rootModel'
import type { Attachment, AttachmentGateway, UploadFileGateway } from './uploadModels'
import type { Receivable } from './receivableModels'
import { nullableThenRequired } from '@/shared/utils/modelUtils'
import { dateToISOStringWithTimeZoneOffset } from '@/shared/utils/dateUtils'
import { removeEmailWhitespace } from '@/shared/utils/inputUtils'

export const invoiceOwners = ['company', 'user'] as const
export type InvoiceOwner = (typeof invoiceOwners)[number]

export const invoiceStatuses = ['OPEN', 'REPAID', 'PAST_DUE', 'CANCELED'] as const
export type InvoiceStatus = (typeof invoiceStatuses)[number]

// makalu-api models/BaseInvoice.ts and models/Invoice.ts
export type InvoiceGateway = Root & {
    companyId: Company['id']
    attachments?: AttachmentGateway[]
    currency: Currency
    totalAmountIncludingTaxes: number
    totalAmountExcludingTaxes?: number
    invoiceDate: string // Date in ISO 8601 UTC format
    invoiceNumber: string
    debtorName: string
    debtorIdentifier: Identifier
    repaymentPeriod: number
    paymentTermType: PaymentTermOption
    dueDate: string // Date in ISO 8601 UTC format
    loans?: LoanGateway[]
    contacts?: Contact[]
    invoiceOwner?: InvoiceOwner
    debtorId: string | null
    status: InvoiceStatus
    receivableIds?: Receivable['id'][]
}

export type Invoice = Omit<InvoiceGateway, 'attachments'> & {
    isEditable: boolean
    attachments?: Attachment[]
    attachmentCount?: number
}

export function isInvoice(doc: Invoice | Receivable | undefined): doc is Invoice {
    return doc !== undefined && 'invoiceDate' in doc
}

export type CreateInvoiceGateway = Pick<
    InvoiceGateway,
    | 'debtorName'
    | 'totalAmountIncludingTaxes'
    | 'totalAmountExcludingTaxes'
    | 'invoiceDate'
    | 'invoiceNumber'
    | 'debtorIdentifier'
    | 'repaymentPeriod'
    | 'paymentTermType'
    | 'currency'
> & {
    attachments: AttachmentGateway[] | undefined
    contacts: Contact[]
    invoiceOwner: InvoiceOwner | undefined
}

export type UpdateInvoice = Pick<Invoice, 'invoiceNumber' | 'repaymentPeriod' | 'paymentTermType' | 'invoiceOwner'> & {
    debtorId?: string
    attachments: File[]
    invoiceDate?: string
    invoiceId?: Invoice['id']
    debtorName: Invoice['debtorName']
    totalAmountIncludingTaxes?: number
    totalAmountExcludingTaxes?: number
    debtorIdentifier: Invoice['debtorIdentifier']
    currency?: Currency
    contacts: Contact[]
    receivables?: Receivable['id'][]
}

export type UnlinkReceivable = {
    invoiceId: Invoice['id'] | null | undefined
    receivableIdToUnlink: Receivable['id'] | null | undefined
    currentReceivableIds: Receivable['id'][]
}

export type LinkReceivable = {
    invoiceId: Invoice['id'] | undefined
    receivableIdsToLink: Receivable['id'][] | undefined
    currentReceivableIds: Receivable['id'][]
}

export type UnlinkReceivablePayload = {
    receivableIds: Receivable['id'][]
}

export type LinkReceivablePayload = {
    receivableIds: Receivable['id'][]
}

export type UpdateAttachments<T extends { id: string; attachments?: Attachment[] }> = Pick<T, 'id' | 'attachments'> & {
    attachment: Attachment
}

export type UpdateInvoiceAttachments = UpdateAttachments<Invoice>

export type UpdateInvoiceGateway = Partial<
    Pick<
        InvoiceGateway,
        | 'totalAmountIncludingTaxes'
        | 'totalAmountExcludingTaxes'
        | 'invoiceDate'
        | 'invoiceNumber'
        | 'debtorName'
        | 'debtorIdentifier'
        | 'repaymentPeriod'
        | 'paymentTermType'
        | 'contacts'
    >
> & {
    debtorId?: string
    attachments: AttachmentGateway[] | undefined
    currency?: Invoice['currency']
    invoiceOwner?: 'user' | 'company'
}

export const CreateInvoiceFormSchema = z.object({
    invoiceNumber: z.string().min(1),
    invoiceDate: z.date(),
    totalAmountIncludingTaxes: z.number().min(0.01),
    totalAmountExcludingTaxes: z.number().min(0.01),
    invoiceOwner: z.enum(invoiceOwners).optional(),
    contacts: z.array(ContactSchema).min(1),
    attachments: z.array(z.instanceof(File)),
    paymentTermType: nullableThenRequired(z.enum(paymentTermOptionsList)),
    repaymentPeriod: nullableThenRequired(z.number()),
    debtorName: z.string().min(1)
})

export type CreateInvoiceForm = z.infer<typeof CreateInvoiceFormSchema>

export type CreateInvoice = {
    invoiceNumber: string
    invoiceDate: string
    currency: Currency
    totalAmountExcludingTaxes: number
    totalAmountIncludingTaxes: number
    attachments: File[]
    contacts: Contact[]
    repaymentPeriod: number
    paymentTermType: PaymentTermOption
    invoiceOwner: InvoiceOwner | undefined
    debtorName: string
    debtorIdentifier: Identifier
}

export const toCreateInvoiceBody = (payload: CreateInvoice, files?: UploadFileGateway[]): CreateInvoiceGateway => {
    return {
        debtorIdentifier: payload.debtorIdentifier,
        debtorName: payload.debtorName,
        invoiceDate: dateToISOStringWithTimeZoneOffset(startOfDay(new Date(payload.invoiceDate))),
        invoiceOwner: payload.invoiceOwner,
        invoiceNumber: payload.invoiceNumber,
        repaymentPeriod: payload.repaymentPeriod,
        paymentTermType: payload.paymentTermType,
        totalAmountExcludingTaxes: Number(payload.totalAmountExcludingTaxes) || undefined,
        totalAmountIncludingTaxes: Number(payload.totalAmountIncludingTaxes),
        currency: payload.currency,
        contacts: payload.contacts.map((contact) => removeEmailWhitespace(contact)),
        attachments: !isEmpty(files) && !isNil(files) ? files.map((file) => ({ url: file.url })) : undefined
    }
}

export const toUpdateInvoiceBody = (payload: UpdateInvoice, files?: UploadFileGateway[]): UpdateInvoiceGateway => {
    const debtorIdentifier = payload.debtorIdentifier

    return {
        paymentTermType: payload.paymentTermType,
        totalAmountExcludingTaxes: Number(payload.totalAmountExcludingTaxes),
        totalAmountIncludingTaxes: Number(payload.totalAmountIncludingTaxes),
        debtorName: payload.debtorName,
        debtorIdentifier,
        debtorId: payload.debtorId,
        invoiceDate: payload.invoiceDate ? dateToISOStringWithTimeZoneOffset(new Date(payload.invoiceDate)) : undefined,
        invoiceNumber: payload.invoiceNumber,
        repaymentPeriod: payload.repaymentPeriod,
        currency: payload.currency,
        contacts: payload.contacts?.map((contact) => removeEmailWhitespace(contact)) ?? [],
        attachments: !isEmpty(files) && !isNil(files) ? files.map((file) => ({ url: file.url })) : [],
        invoiceOwner: payload.invoiceOwner
    }
}
